import formatPercent from './formatPercent';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import { useDeleteWithholding_docMutation } from '../withholding_docsApiSlice';
import Withholding_docRow from './Withholding_docRow';

function formatDate(value) {
  if (!value) return '—';
  try {
    return format(new Date(value), 'yyyy-MM-dd');
  } catch {
    return '—';
  }
}

function formatCurrency(value) {
  return `Rp ${Number(value || 0).toLocaleString('id-ID')}`;
}

function StatusBadge({ status }) {
  if (status === 'NORMAL') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700 ring-1 ring-inset ring-emerald-200">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500"></span>
        NORMAL
      </span>
    );
  }

  if (status === 'PEMBETULAN') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700 ring-1 ring-inset ring-amber-200">
        <span className="h-1.5 w-1.5 rounded-full bg-amber-500"></span>
        PEMBETULAN
      </span>
    );
  }

  if (status === 'DIBATALKAN') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2.5 py-1 text-xs font-medium text-rose-700 ring-1 ring-inset ring-rose-200">
        <span className="h-1.5 w-1.5 rounded-full bg-rose-500"></span>
        DIBATALKAN
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-700 ring-1 ring-inset ring-gray-200">
      {status || 'UNKNOWN'}
    </span>
  );
}

function MobileCard({ withholding_doc }) {
  const [deleteWithholding_doc, { isLoading }] =
    useDeleteWithholding_docMutation();

  const handleDelete = async () => {
    const ok = window.confirm(
      `Delete withholding doc "${withholding_doc.doc_number || withholding_doc.withholding_doc_id}"?`
    );

    if (!ok) return;

    try {
      await deleteWithholding_doc({ id: withholding_doc.id }).unwrap();
    } catch (err) {
      console.error('Delete error:', err);
      alert(err?.data?.message || 'Failed to delete withholding doc');
    }
  };

  return (
    <li className="p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-medium text-gray-900">
            {withholding_doc.doc_number || withholding_doc.withholding_doc_id}
          </div>
          <div className="text-sm text-gray-500">
            {withholding_doc.payer_name || 'No payer'}
          </div>
        </div>

        <StatusBadge status={withholding_doc.status_bukti_pemotongan} />
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2 text-sm text-gray-600">
        <div>
          <span className="text-gray-400">Doc Date:</span>{' '}
          {formatDate(withholding_doc.doc_date)}
        </div>
        <div>
          <span className="text-gray-400">Sifat:</span>{' '}
          {withholding_doc.sifat_pemotongan || '—'}
        </div>
        <div>
          <span className="text-gray-400">Gross:</span>{' '}
          {formatCurrency(withholding_doc.gross_income)}
        </div>
        <div>
          <span className="text-gray-400">Tax:</span>{' '}
          {formatCurrency(withholding_doc.tax_withheld)}
        </div>
      </div>

      <div className="mt-2 grid grid-cols-2 gap-2 text-sm text-gray-600">
        <div><span className="text-gray-400">DPP:</span> {formatPercent(withholding_doc.dpp_percent)}</div>
        <div><span className="text-gray-400">Tax Rate:</span> {formatPercent(withholding_doc.tax_rate_percent)}</div>
      </div>

      <div className="mt-3 flex justify-end gap-2">
        <Link
          to={`/dash/withholding_docs/${withholding_doc.id}`}
          className="inline-flex items-center rounded-xl border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
        >
          Edit
        </Link>

        <button
          onClick={handleDelete}
          disabled={isLoading}
          className="inline-flex items-center rounded-xl border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
        >
          {isLoading ? 'Deleting...' : 'Delete'}
        </button>
      </div>
    </li>
  );
}

export default function Withholding_docTable({ withholding_docs, loading }) {
  if (loading) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="mb-4 h-5 w-32 rounded bg-gray-100" />
        <div className="space-y-2">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-12 rounded-xl bg-gray-50" />
          ))}
        </div>
      </div>
    );
  }

  if (!withholding_docs?.length) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-white p-10 text-center text-gray-500 shadow-sm">
        Tidak ada Bukti Potong ditemukan (No withholding docs found)
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
      <div className="hidden md:block overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50/60">
            <tr>
              <Th>#</Th>
              <Th>Doc ID</Th>
              <Th>Document</Th>
              <Th>Payer</Th>
              <Th>Payer NPWP</Th>
              <Th>Sifat</Th>
              <Th>Status</Th>
              <Th>Gross Income</Th>
              <Th>Pengurang Penghasilan</Th>
              <Th>DPP (%)</Th>
              <Th>Net Income</Th>
              <Th>Tax Rate (%)</Th>
              <Th>Tax Withheld</Th>
              <Th>Tax Year</Th>
              <Th>Tax Month</Th>
              <Th className="pr-6 text-right">Actions</Th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-100">
            {withholding_docs.map((withholding_doc,index) => (
              <Withholding_docRow
                key={withholding_doc.id}
                withholding_doc={withholding_doc}
                index={index}
              />
            ))}
          </tbody>
        </table>
      </div>

      <ul className="divide-y divide-gray-100 md:hidden">
        {withholding_docs.map((withholding_doc) => (
          <MobileCard
            key={withholding_doc.id}
            withholding_doc={withholding_doc}
          />
        ))}
      </ul>
    </div>
  );
}

function Th({ children, className = '' }) {
  return (
    <th
      className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 ${className}`}
    >
      {children}
    </th>
  );
}